import { ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DamWeekDayKey } from '~/providers/types/api-private';
import { colors } from '~/styles/colors';
import { CheckBox } from '../../ds';
import { FULL_DAY, PROGRAMMING_TITLE_MAX_LENGTH, SchedulePermission } from '../../schedules';
import { DayChips, Toggle } from '../controls';
import { CheckIcon } from '../icons';
import { ModalShell } from './ModalShell';
import {
  CancelLink, InlineCheck, ModalBody, ModalDivider, ModalError, ModalLabel, ModalSubtitle, ModalTitle,
  PermissionLabel, PermissionRow, SubmitButton, TextInput, TimeField, TimeInput, TimeRow,
} from './styles';

export interface ScheduleModalValues {
  title: string;
  days: DamWeekDayKey[];
  start: string;
  end: string;
  fullDay: boolean;
  permission: SchedulePermission;
}

export const EMPTY_SCHEDULE_VALUES: ScheduleModalValues = {
  title: '', days: [], start: '', end: '', fullDay: false, permission: 'allow',
};

interface ScheduleModalProps {
  initialValues: ScheduleModalValues;
  isEdit: boolean;
  onClose: () => void;
  onSubmit: (values: ScheduleModalValues) => void;
}

export function ScheduleModal({
  initialValues, isEdit, onClose, onSubmit,
}: Readonly<ScheduleModalProps>): ReactElement {
  const { t } = useTranslation();
  const {
    control, register, handleSubmit, watch, formState: { errors },
  } = useForm<ScheduleModalValues>({ defaultValues: initialValues });
  const fullDay = watch('fullDay');

  function submit(values: ScheduleModalValues): void {
    onSubmit(values.fullDay ? { ...values, start: FULL_DAY.start, end: FULL_DAY.end } : values);
  }

  return (
    <ModalShell width={560} onClose={onClose}>
      <ModalBody onSubmit={handleSubmit(submit)} noValidate data-testid="eco-simplificado-modal-programacao">
        <ModalTitle>{t(isEdit ? 'ecoSimplificadoEditarProgramacao' : 'ecoSimplificadoAdicionarProgramacaoTitulo')}</ModalTitle>
        <ModalSubtitle>{t('ecoSimplificadoProgramacaoSubtitulo')}</ModalSubtitle>

        <ModalLabel htmlFor="eco-simplificado-titulo">{t('ecoSimplificadoTitulo')}</ModalLabel>
        <TextInput
          id="eco-simplificado-titulo"
          placeholder={t('ecoSimplificadoDigiteTitulo')}
          {...register('title', {
            validate: (value) => value.trim().length <= PROGRAMMING_TITLE_MAX_LENGTH
              || t('ecoSimplificadoTituloMuitoLongo', { max: PROGRAMMING_TITLE_MAX_LENGTH }),
          })}
        />
        {errors.title ? <ModalError>{errors.title.message}</ModalError> : null}

        <ModalDivider />

        <ModalLabel as="div" style={{ marginTop: 0, marginBottom: 10 }}>{t('selecioneDias')}</ModalLabel>
        <Controller
          control={control}
          name="days"
          rules={{ validate: (value) => value.length > 0 || t('selecioneAoMenosUmDia') }}
          render={({ field }) => (
            <DayChips
              selected={field.value}
              onToggle={(day) => field.onChange(field.value.includes(day) ? field.value.filter((item) => item !== day) : [...field.value, day])}
            />
          )}
        />
        {errors.days ? <ModalError>{errors.days.message}</ModalError> : null}

        <TimeRow>
          <TimeField>
            <ModalLabel htmlFor="eco-simplificado-inicio">{t('ecoSimplificadoHorarioInicio')}</ModalLabel>
            <TimeInput
              id="eco-simplificado-inicio"
              type="time"
              disabled={fullDay}
              {...register('start', {
                validate: (value, values) => values.fullDay || !!value || t('obrigatorio'),
              })}
            />
            {errors.start ? <ModalError>{errors.start.message}</ModalError> : null}
          </TimeField>
          <TimeField>
            <ModalLabel htmlFor="eco-simplificado-fim">{t('ecoSimplificadoHorarioTermino')}</ModalLabel>
            <TimeInput
              id="eco-simplificado-fim"
              type="time"
              disabled={fullDay}
              {...register('end', {
                validate: (value, values) => {
                  if (values.fullDay) return true;
                  if (!value) return t('obrigatorio');
                  return !values.start || value > values.start || t('horarioFinalMaiorInicial');
                },
              })}
            />
            {errors.end ? <ModalError>{errors.end.message}</ModalError> : null}
          </TimeField>
          <InlineCheck>
            <input type="checkbox" {...register('fullDay')} />
            <CheckBox $checked={fullDay}><CheckIcon size={12} strokeWidth={3} color={colors.text['white-0']} /></CheckBox>
            {t('ecoSimplificadoDiaInteiro')}
          </InlineCheck>
        </TimeRow>

        <ModalLabel as="div" style={{ marginTop: 22, marginBottom: 10 }}>{t('funcionamento')}</ModalLabel>
        <Controller
          control={control}
          name="permission"
          render={({ field }) => (
            <PermissionRow>
              <PermissionLabel $active={field.value === 'allow'}>{t('ecoSimplificadoPermitirRefrigeracao')}</PermissionLabel>
              <Toggle
                checked={field.value === 'allow'}
                onChange={(allow) => field.onChange(allow ? 'allow' : 'forbid')}
                label={t('funcionamento')}
                testId="eco-simplificado-funcionamento"
              />
              <PermissionLabel $active={field.value === 'forbid'}>{t('ecoSimplificadoBloquear')}</PermissionLabel>
            </PermissionRow>
          )}
        />

        <SubmitButton type="submit">{t(isEdit ? 'ecoSimplificadoSalvar' : 'botaoAdicionar')}</SubmitButton>
        <CancelLink type="button" onClick={onClose}>{t('cancelar')}</CancelLink>
      </ModalBody>
    </ModalShell>
  );
}
